import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ResidueType } from "../../types/residue.type";
import { api } from "../../api/api";
import {
  Button,
  Checkbox,
  Input,
  message,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { useAuth } from "../../contexts/auth.context";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { formatNumber } from "../../utils/formatNumber";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function Residue() {
  const { authState } = useAuth();
  const { type } = useParams();
  const [data, setData] = useState<ResidueType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [hasResidue, setHasResidue] = useState<boolean>(true);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const fetchResidue = async () => {
    setLoading(true);
    try {
      const response = await api.get("/residue", {
        params: {
          page,
          type,
          limit: pageSize,
          search: searchTerm,
          hasResidue: hasResidue ? true : undefined,
          sortBy,
          sortOrder,
        },
      });

      const { data, total } = response.data;

      setData(data);
      setTotal(total);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение остатка.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResidue();
  }, [page, pageSize, searchTerm, type, hasResidue, sortBy, sortOrder]);

  const handleExport = () => {
    downloadFile("/export/residues", "residue_data.xlsx", { type });
  };



  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      fixed: "left",
    },
    {
      title: "Ед. изм.",
      dataIndex: "unitOfMeasurement",
      key: "unitOfMeasurement",
    },
    {
      title: "Остаток",
      dataIndex: "residue",
      key: "residue",
      render: (_: any, record: ResidueType) => {
        const { residue, lowerLimit, upperLimit } = record;

        let backgroundColor = "";
        if (lowerLimit !== undefined && upperLimit !== undefined) {
          if (residue < lowerLimit) {
            backgroundColor = "red";
          } else if (residue <= upperLimit) {
            backgroundColor = "yellow";
          } else if (residue > upperLimit) {
            backgroundColor = "green";
          }
        }

        return {
          props: {
            style: { background: backgroundColor },
          },
          children: <div>{roundToThreeDecimals(residue)}</div>,
        };
      },
    },
    // Добавляем колонку "Остаток КГ" только для продукции
    ...(type === "produces" ? [{
      title: "Остаток (2)",
      dataIndex: "residueKg",
      key: "residueKg",
      render: (_: any, record: ResidueType) => {
        const residueKg = record.residueKg || 0;
        const unit = record.residueKgUnit || "кг";
        return `${roundToThreeDecimals(residueKg)} ${unit}`;
      },
    }] : []),
    {
      title: "Средняя Себестоимость USD",
      key: "averageCostUSD",
      render: (_: any, record: ResidueType) => {
        return record.averageCost
          ? `${formatNumber(record.averageCost.averageCostUSD)}`
          : "Н/Д";
      },
    },
    {
      title: "Средняя Себестоимость TJS",
      key: "averageCostTJS",
      render: (_: any, record: ResidueType) => {
        return record.averageCost
          ? `${formatNumber(record.averageCost.averageCostTJS)}`
          : "Н/Д";
      },
    },
    {
      title: "Прибыль (%) USD",
      key: "profitPercentageUSD",
      render: (_: any, record: ResidueType) => {
        return record.averageCost
          ? `${record.averageCost.profitPercentageUSD}%`
          : "Н/Д";
      },
    },
    {
      title: "Прибыль (%) TJS",
      key: "profitPercentageTJS",
      render: (_: any, record: ResidueType) => {
        return record.averageCost
          ? `${record.averageCost.profitPercentageTJS}%`
          : "Н/Д";
      },
    },
  ];



  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const rowSelection = {
    selectedRowKeys: selectedItems,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedItems(selectedRowKeys as string[]);
    },
  };

  const handleDownloadSelectedOrders = () => {
    if (selectedItems.length === 0) {
      message.warning("Пожалуйста, выберите хотя бы один отчет.");
      return;
    }

    const selectedItemsIds = selectedItems.join(",");

    downloadFile(
      `/residue/export?selectedItems=${selectedItemsIds}&type=${type}`,
      `residue_data.pdf`
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <Title>Остатки</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Select
          placeholder="Сортировать по"
          defaultValue="name"
          onChange={(value) => setSortBy(value)}
          style={{ width: 150 }}
        >
          <Option value="name">Название</Option>
          <Option value="totalProduced">Произведено</Option>
          <Option value="createdAt">Дата</Option>
          <Option value="residue">Остаток</Option>
          <Option value="totalSold">Кол-во продаж</Option>
          <Option value="totalPaidAmount">Кол-во покупок</Option>
        </Select>
        <Select
          defaultValue="asc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 120 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
        <Checkbox
          checked={hasResidue}
          onChange={(e) => setHasResidue(e.target.checked)}
        >
          Имеет остаток
        </Checkbox>
        <Button
          type="primary"
          loading={downloadLoading}
          onClick={handleDownloadSelectedOrders}
          disabled={selectedItems.length === 0}
        >
          Получить отчет
        </Button>
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>

      </Space>

      <Table
        // @ts-ignore
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total,
          pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
        rowSelection={rowSelection}
      />
    </div>
  );
}
